import { type ReactNode, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { hasPin, isUnlocked, markLocked } from '../lib/quicklock';
import { useSession } from '../lib/session';
import { PinLockScreen } from './PinLockScreen';

/**
 * Protege las pantallas privadas.
 *
 * No es la defensa de seguridad —esa está en el servidor, que exige sesión en
 * todas las rutas de datos—, sino la forma de mandar al usuario a la pantalla
 * de entrada en lugar de mostrarle una aplicación vacía.
 *
 * Aquí también vive el **bloqueo con PIN**, porque es el único sitio por el que pasa
 * todo lo privado: si estuviera en cada pantalla, olvidarlo en una sola dejaría las
 * cifras a la vista justo en la que se olvidó.
 */
export function RequireAuth({ children }: { children: ReactNode }): React.JSX.Element {
  const status = useSession((s) => s.status);
  const location = useLocation();

  /*
   * El bloqueo se comprueba **al montar** y no en cada dibujado.
   *
   * Si se comprobara en cada dibujado, al desbloquear volvería a pedir el PIN antes de
   * que `markUnlocked` se hubiera escrito, y el usuario vería la pantalla de bloqueo
   * parpadear sin entender por qué.
   */
  const [locked, setLocked] = useState(() => hasPin() && !isUnlocked());

  if (status !== 'authenticated') {
    // Al salir de la sesión, el bloqueo se rearma: la próxima vez que entre alguien,
    // aunque sea el mismo, se le pide el PIN otra vez.
    markLocked();
    return <Navigate to="/entrar" replace state={{ from: location.pathname }} />;
  }

  if (locked) {
    return <PinLockScreen onUnlocked={() => setLocked(false)} />;
  }

  return <>{children}</>;
}
